import React from 'react';

import PriceDistributionView from '../../components/PriceDistributionView';

const ExpandedRunHistoryRows = ({
  controller,
  run,
  res,
  ridx,
  hist,
  hidx,
  runMinConfidenceThreshold,
}) => {
  const {
    getFilteredItemEntries,
    getResultImageUrls,
    getDisabledImageKeySet,
    toFiniteNumber,
    getResultTokenCounts,
    getResultAttemptSourceInfo,
    computeAccuracyRatio,
    normalizeImageKey,
    setSelectedImage,
    setSelectedImages,
    setSelectedLotNum,
    setSelectedItems,
    getResultLotNumber,
    formatConfidencePercent,
    getReviewNoteDraftKey,
    getTrustedReviewNote,
    reviewNoteDrafts,
    setReviewNoteDrafts,
    savingReviewNotes,
    saveReviewNote,
    lotNoteDrafts,
    setLotNoteDrafts,
    savingLotNotes,
    saveLotReviewNote,
    getSourceBadgeClassName,
    getSourceDisplayLabel,
    toFiniteCount,
    formatTokenCount,
    formatCostDisplay,
  } = controller;

  const runId = String(run?.run_id || '').trim();
  const histItemEntries = getFilteredItemEntries(hist?.items, runMinConfidenceThreshold);
  if (histItemEntries.length === 0) return null;

  const histItemCount = histItemEntries.length;
  const firstHistItem = (histItemEntries.find((entry) => entry.item)?.item) || null;
  const histImageUrls = getResultImageUrls(hist, firstHistItem);
  const histDisabledKeys = getDisabledImageKeySet(hist);
  const visibleHistItemEntries = histItemEntries.filter(
    (entry) => entry.item && typeof entry.item === 'object',
  );
  const histEstSum = histItemEntries.reduce(
    (sum, entry) => (
      entry.item && typeof entry.item === 'object'
        ? (sum + (toFiniteNumber(entry?.item?.estimated) || 0))
        : sum
    ),
    0,
  );
  const histActual = visibleHistItemEntries
    .map((entry) => toFiniteNumber(entry?.item?.actual))
    .find((value) => value !== null) ?? null;
  const histTokens = getResultTokenCounts(hist);
  const histSourceInfo = getResultAttemptSourceInfo(hist);
  const histAcc = computeAccuracyRatio(histActual, histEstSum);
  const lotNumberForHistory = getResultLotNumber(res, firstHistItem) || getResultLotNumber(res) || ridx;

  return (
    <React.Fragment>
      {histItemEntries.map(({ item: hItem, originalIndex: histOriginalIndex }, hiidx) => {
        const hi = hItem && typeof hItem === 'object' ? hItem : {};
        const isHistFilteredOutRow = histOriginalIndex === null;
        const hasConcreteHistIndex = Number.isFinite(Number(histOriginalIndex));
        const hiEst = toFiniteNumber(hi.estimated);

        const histReviewDraftKey = hasConcreteHistIndex
          ? getReviewNoteDraftKey({
              runId,
              lotNumber: lotNumberForHistory,
              resultIndex: ridx,
              itemIndex: histOriginalIndex,
              historyIndex: hidx,
            })
          : '';
        const histPersistedNote = hasConcreteHistIndex ? getTrustedReviewNote(hi) : '';
        const histNoteValue = hasConcreteHistIndex && Object.prototype.hasOwnProperty.call(reviewNoteDrafts, histReviewDraftKey)
          ? reviewNoteDrafts[histReviewDraftKey]
          : histPersistedNote;
        const histIsSaving = hasConcreteHistIndex ? !!savingReviewNotes[histReviewDraftKey] : false;
        const histHasChanges = hasConcreteHistIndex ? (histNoteValue !== histPersistedNote) : false;
        const histCanSave = hasConcreteHistIndex && Boolean(runId && lotNumberForHistory);

        return (
          <tr key={`${ridx}-hist-${hidx}-${histOriginalIndex}-${hiidx}`} className={`bg-purple-50/60 ${hiidx < histItemCount - 1 ? '' : 'border-b border-purple-200'}`}>
            {hiidx === 0 && (
              <td className="px-1.5 py-1 border-r align-top bg-purple-100/50" rowSpan={histItemCount}>
                <div className="flex flex-col items-start gap-0.5">
                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-purple-600 text-white font-bold text-[8px] shrink-0">
                    {hidx + 1}
                  </span>
                  {hist.rerun_timestamp && (
                    <span className="text-[8px] text-purple-500 leading-tight text-center">
                      <span className="block">{new Date(hist.rerun_timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                      <span className="block">{new Date(hist.rerun_timestamp).getFullYear()}</span>
                      <span className="block">{new Date(hist.rerun_timestamp).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</span>
                    </span>
                  )}
                </div>
              </td>
            )}
            {hiidx === 0 && (
              <td className="px-1.5 py-1 border-r align-top" rowSpan={histItemCount}>
                <div className="flex flex-wrap gap-1">
                  {histImageUrls.map((url, huidx) => {
                    const hImgKey = normalizeImageKey(url);
                    const hIsDisabled = histDisabledKeys.has(hImgKey);
                    return (
                      <div key={huidx} className="relative">
                        <img
                          src={url}
                          alt={`hist ${huidx + 1}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedImage(url);
                            setSelectedImages(histImageUrls);
                            setSelectedLotNum(lotNumberForHistory);
                            setSelectedItems(hist.items);
                          }}
                          className={`w-10 h-10 object-cover rounded shadow-sm hover:scale-[1.5] hover:z-10 transition-transform cursor-pointer ${hIsDisabled ? 'grayscale opacity-35' : ''}`}
                        />
                        {hIsDisabled && (
                          <span className="absolute inset-0 flex items-center justify-center text-[7px] font-bold text-white bg-slate-900/55 rounded">OFF</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </td>
            )}
            <td className="px-1.5 py-1 border-r font-medium">
              {isHistFilteredOutRow ? (
                <span className="text-slate-400">-</span>
              ) : (
                <div className="break-words" title={hi.item || hi.item_name || '-'}>{hi.item || hi.item_name || '-'}</div>
              )}
            </td>
            <td className="px-1.5 py-1 border-r">
              {isHistFilteredOutRow ? <span className="text-slate-300">-</span> : (hiEst === null ? '-' : `$${hiEst.toFixed(2)}`)}
            </td>
            {hiidx === 0 && (
              <td className="px-1.5 py-1 border-r align-top font-bold" rowSpan={histItemCount}>
                ${histEstSum.toFixed(2)}
              </td>
            )}
            {hiidx === 0 && (
              <td className="px-1.5 py-1 border-r align-top" rowSpan={histItemCount}>
                {histActual === null ? '-' : `$${histActual.toFixed(2)}`}
              </td>
            )}
            <td className="px-1.5 py-1 border-r text-slate-600">
              {isHistFilteredOutRow ? (
                <div className="text-[10px] text-slate-400 italic">Filtered out by confidence</div>
              ) : (
                (() => {
                  const hCondition = hi?.condition || 'Unknown';
                  const hPriceSource = hi?.price_source || 'model_knowledge';
                  const hConfidenceLabel = formatConfidencePercent(hi?.confidence_score, '-');
                  const hSourceLabel = hPriceSource === 'ebay_rag' ? 'eBay RAG' : hPriceSource === 'image' ? 'Image' : 'Model Knowledge';
                  const hSourceBg = hPriceSource === 'ebay_rag' ? 'bg-blue-100 text-blue-700' : hPriceSource === 'image' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
                  const hCondBg = hCondition === 'New' ? 'bg-green-100 text-green-700' : hCondition === 'Damaged' || hCondition === 'For Parts' ? 'bg-red-100 text-red-700' : hCondition === 'Unknown' ? 'bg-slate-100 text-slate-500' : 'bg-yellow-100 text-yellow-700';
                  return (
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1"><span className="text-[8px] text-slate-400">Condition:</span><span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${hCondBg}`}>{hCondition}</span></div>
                      <div className="flex items-center gap-1"><span className="text-[8px] text-slate-400">Price Src:</span><span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${hSourceBg}`}>{hSourceLabel}</span></div>
                      <div className="flex items-center gap-1"><span className="text-[8px] text-slate-400">Conf:</span><span className="inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold bg-indigo-100 text-indigo-700">{hConfidenceLabel}</span></div>
                      <PriceDistributionView
                        rangeValue={hi?.estimated_value_range}
                        distributionValue={hi?.price_estimate_distribution}
                        maxBins={Number.MAX_SAFE_INTEGER}
                        compact
                        className="mt-0.5"
                      />
                    </div>
                  );
                })()
              )}
            </td>
            <td className="px-1.5 py-1 border-r align-top">
              {isHistFilteredOutRow ? (
                <span className="text-[10px] text-slate-400">-</span>
              ) : (
                <div className="space-y-1">
                  <textarea
                    value={histNoteValue}
                    onChange={(event) => {
                      const nextValue = event.target.value.slice(0, 4000);
                      setReviewNoteDrafts((previous) => ({ ...previous, [histReviewDraftKey]: nextValue }));
                    }}
                    maxLength={4000}
                    placeholder="Add subitem note..."
                    className="w-full min-h-[48px] rounded border border-purple-300 px-1.5 py-1 text-[10px] outline-none focus:border-purple-500 bg-purple-50/30"
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-slate-400">{histNoteValue.length}/4000</span>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!histCanSave || histIsSaving || !histHasChanges) return;
                        saveReviewNote({
                          runId,
                          lotNumber: lotNumberForHistory,
                          resultIndex: ridx,
                          itemIndex: histOriginalIndex,
                          historyIndex: hidx,
                          reviewNote: histNoteValue,
                        });
                      }}
                      disabled={!histCanSave || histIsSaving || !histHasChanges}
                      className={`rounded px-2 py-0.5 text-[10px] font-semibold transition-colors ${!histCanSave || histIsSaving || !histHasChanges ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200' : 'bg-purple-600 text-white hover:bg-purple-700 border border-purple-600'}`}
                    >
                      {histIsSaving ? 'Saving...' : 'Save Note'}
                    </button>
                  </div>
                </div>
              )}
            </td>
            {hiidx === 0 && (() => {
              const histLotDraftKey = `lot-${runId}-${lotNumberForHistory || ridx}-${ridx}-h${hidx}`;
              const histPersistedLotNote = (typeof hist.lot_review_note === 'string') ? hist.lot_review_note : '';
              const histLotNoteValue = Object.prototype.hasOwnProperty.call(lotNoteDrafts, histLotDraftKey) ? lotNoteDrafts[histLotDraftKey] : histPersistedLotNote;
              const histIsSavingLotNote = !!savingLotNotes[histLotDraftKey];
              const histHasLotNoteChanges = histLotNoteValue !== histPersistedLotNote;
              const histLotCanSave = Boolean(runId && lotNumberForHistory);
              return (
                <td className="px-1.5 py-1 border-r align-top" rowSpan={histItemCount}>
                  <div className="space-y-1">
                    <textarea
                      value={histLotNoteValue}
                      onChange={(e) => {
                        const v = e.target.value.slice(0, 4000);
                        setLotNoteDrafts((prev) => ({ ...prev, [histLotDraftKey]: v }));
                      }}
                      maxLength={4000}
                      placeholder="Add item note..."
                      className="w-full min-h-[48px] rounded border border-purple-300 px-1.5 py-1 text-[10px] outline-none focus:border-purple-500 bg-purple-50/30"
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-slate-400">{histLotNoteValue.length}/4000</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!histLotCanSave || histIsSavingLotNote || !histHasLotNoteChanges) return;
                          saveLotReviewNote({
                            runId,
                            lotNumber: lotNumberForHistory,
                            resultIndex: ridx,
                            historyIndex: hidx,
                            reviewNote: histLotNoteValue,
                          });
                        }}
                        disabled={!histLotCanSave || histIsSavingLotNote || !histHasLotNoteChanges}
                        className={`rounded px-2 py-0.5 text-[10px] font-semibold transition-colors ${!histLotCanSave || histIsSavingLotNote || !histHasLotNoteChanges ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200' : 'bg-purple-600 text-white hover:bg-purple-700 border border-purple-600'}`}
                      >
                        {histIsSavingLotNote ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                </td>
              );
            })()}
            {hiidx === 0 && (
              <td className="px-1.5 py-1 border-r align-top" rowSpan={histItemCount}>
                {histAcc !== null ? (
                  <span className={`px-1 py-0.5 rounded font-bold text-[10px] ${histAcc > 0.8 ? 'bg-green-100 text-green-700' : histAcc > 0.5 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                    {(histAcc * 100).toFixed(1)}%
                  </span>
                ) : '-'}
              </td>
            )}
            {hiidx === 0 && (() => {
              const histDurSec = toFiniteNumber(hist.duration);
              const histDurMs = toFiniteNumber(hist.duration_ms);
              const histDur = histDurMs > 0 ? histDurMs / 1000 : (histDurSec > 0 ? histDurSec : 0);
              const liveDur = toFiniteNumber(hist.last_live_duration);
              const displayDur = histDur > 0 ? histDur : liveDur;
              return (
                <td className="px-1.5 py-1 border-r align-top" rowSpan={histItemCount}>
                  {displayDur != null && displayDur > 0 ? `${Number(displayDur).toFixed(1)}s` : '-'}
                </td>
              );
            })()}
            {hiidx === 0 && (
              <td className="px-1.5 py-1 border-r align-top" rowSpan={histItemCount}>
                <span
                  className={`inline-flex rounded px-1 py-0.5 text-[9px] font-semibold whitespace-nowrap ${getSourceBadgeClassName(histSourceInfo.finalSource, histSourceInfo.finalCacheHit)}`}
                  title={histSourceInfo.cacheKey || undefined}
                >
                  {histSourceInfo.finalCacheHit === null ? getSourceDisplayLabel(histSourceInfo.finalSource) : (histSourceInfo.finalCacheHit ? 'CACHE' : 'LIVE')}
                </span>
              </td>
            )}
            {hiidx === 0 && (
              <td className="px-1.5 py-1 border-r align-top" rowSpan={histItemCount}>
                <span className={`inline-flex rounded px-1.5 py-0.5 text-[9px] font-semibold ${toFiniteCount(histTokens.input) !== null ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
                  {formatTokenCount(histTokens.input)}
                </span>
              </td>
            )}
            {hiidx === 0 && (
              <td className="px-1.5 py-1 border-r align-top" rowSpan={histItemCount}>
                <span className={`inline-flex rounded px-1.5 py-0.5 text-[9px] font-semibold ${toFiniteCount(histTokens.output) !== null ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-500'}`}>
                  {formatTokenCount(histTokens.output)}
                </span>
              </td>
            )}
            {hiidx === 0 && (
              <td className="px-1.5 py-1 align-top" rowSpan={histItemCount}>
                {formatCostDisplay(hist.cost)}
              </td>
            )}
          </tr>
        );
      })}
    </React.Fragment>
  );
};

export default ExpandedRunHistoryRows;
